import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const RolePermissionsContext = createContext({
  permissions: {},
  roleName: '',
  displayName: '',
  loading: true,
  canViewPage: () => true,
  canEditPage: () => true,
  refreshPermissions: () => {},
});

export const useRolePermissions = () => {
  const context = useContext(RolePermissionsContext);
  if (!context) {
    throw new Error('useRolePermissions must be used within a RolePermissionsProvider');
  }
  return context;
};

export const RolePermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [roleName, setRoleName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions({});
      setRoleName('');
      setDisplayName('');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/roles/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });

      setPermissions(response.data.pages || {});
      setRoleName(response.data.role || '');
      setDisplayName(response.data.display_name || '');
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      // On error, default to permissive (so existing users aren't locked out)
      setPermissions({});
      setRoleName(user?.role || '');
      setDisplayName('');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const canViewPage = useCallback(
    (pageId) => {
      // If permissions loaded and populated, use them strictly
      if (permissions && Object.keys(permissions).length > 0) {
        const page = permissions[pageId];
        if (!page) return false;
        return page.visible === true;
      }
      // Fallback: only permissive for admin/super_admin when permissions haven't loaded
      const userRole = user?.role;
      if (userRole === 'admin' || userRole === 'super_admin') return true;
      // For all other roles, be restrictive when permissions are empty/missing
      return false;
    },
    [permissions, user?.role]
  );

  const canEditPage = useCallback(
    (pageId) => {
      // If permissions loaded and populated, use them strictly
      if (permissions && Object.keys(permissions).length > 0) {
        const page = permissions[pageId];
        if (!page) return false;
        return page.editable === true;
      }
      // Fallback: only permissive for admin/super_admin when permissions haven't loaded
      const userRole = user?.role;
      if (userRole === 'admin' || userRole === 'super_admin') return true;
      // For all other roles, be restrictive when permissions are empty/missing
      return false;
    },
    [permissions, user?.role]
  );

  const value = {
    permissions,
    roleName,
    displayName,
    loading,
    canViewPage,
    canEditPage,
    refreshPermissions: fetchPermissions,
  };

  return (
    <RolePermissionsContext.Provider value={value}>
      {children}
    </RolePermissionsContext.Provider>
  );
};

export default RolePermissionsContext;
